import {
	Fragment,
	HTMLAttributes,
	PropsWithChildren,
	useCallback,
	useEffect,
	useRef,
} from 'react';
import { useEffectOnce } from 'react-use';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction } from '~/shared';
import { Button, ButtonColors, ButtonHeight } from '../Button';
import { ScrollContainer } from '../ScrollContainer';
import { Text } from '../Text';
import {
	StyledButtonContainer,
	StyledButtonContent,
	StyledContainer,
	StyledListContent,
	StyledTopContainer,
} from './ShopListWithScroll.styled';

export interface IShopListProps {
	icon: string;
	title: string;
	handleBack: () => void;
	handleSelect: (index: number | null) => void;
	activeIndex: number | null;
	maxIndex: number;
	handleCheckInstall: (index: number) => boolean;
	height?: number;
	allowSelectInstalled?: boolean;
	enableAutoScroll?: boolean;
	getItemId?: (index: number) => string;
	hideBack?: boolean;
}

const ShopListWithScroll = ({
	children,
	icon,
	title,
	handleBack,
	handleSelect,
	activeIndex,
	maxIndex,
	handleCheckInstall,
	height,
	allowSelectInstalled,
	enableAutoScroll = false,
	getItemId,
	hideBack = false,
	...props
}: PropsWithChildren & HTMLAttributes<HTMLDivElement> & IShopListProps) => {
	const shouldSkipInstalled = !allowSelectInstalled;
	const rafRef = useRef<number | null>(null);
	const scrollContainerRef = useRef<HTMLElement | null>(null);
	const targetScrollTopRef = useRef<number | null>(null);
	const lastTimeRef = useRef<number | null>(null);
	const previousActiveIndex = useRef<number | null>(null);
	const modalState = useObservable(rage.modal.getCurrentModal());

	const ensureScrollAnimationRunning = useCallback(() => {
		if (rafRef.current !== null) return;

		const tick = (time: number) => {
			const container = scrollContainerRef.current;
			const target = targetScrollTopRef.current;
			if (!container || target === null) {
				rAFStop();
				return;
			}

			const now = time;
			const last = lastTimeRef.current ?? now;
			const dt = Math.max(0, Math.min(1 / 60, (now - last) / 1000));
			lastTimeRef.current = now;

			const current = container.scrollTop;
			const diff = target - current;
			const absDiff = Math.abs(diff);

			const baseSmoothing = 18;
			const maxSmoothing = 42;
			const smoothing =
				baseSmoothing +
				Math.min(1, absDiff / 600) * (maxSmoothing - baseSmoothing);
			const factor = 1 - Math.exp(-smoothing * dt);
			const next = current + diff * factor;
			container.scrollTop = next;

			if (Math.abs(target - next) < 0.2) {
				container.scrollTop = target;
				rAFStop();
				return;
			}

			runNext();
		};

		const runNext = () => {
			rafRef.current = requestAnimationFrame(tick);
		};

		const rAFStop = () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			lastTimeRef.current = null;
		};

		runNext();
	}, []);

	const scrollToCenter = useCallback(
		(index: number) => {
			if (!getItemId) return;

			const itemId = getItemId(index);
			const itemDiv = document.getElementById(itemId);
			if (!itemDiv) return;

			const container = itemDiv.closest(
				'[data-scroll-container]',
			) as HTMLElement | null;
			if (!container) {
				itemDiv.scrollIntoView({
					block: 'center',
					inline: 'nearest',
					behavior: 'auto',
				});
				previousActiveIndex.current = index;
				return;
			}
			scrollContainerRef.current = container;

			const maxScrollTop = container.scrollHeight - container.clientHeight;
			let finalScrollTop: number;

			const isCyclicTransition =
				previousActiveIndex.current !== null &&
				previousActiveIndex.current !== index;

			if (isCyclicTransition) {
				const currentIndex = index;
				const prevIndex = previousActiveIndex.current;
				if (prevIndex === maxIndex && currentIndex === 0) {
					finalScrollTop = 0;
				} else if (prevIndex === 0 && currentIndex === maxIndex) {
					finalScrollTop = maxScrollTop;
				} else {
					const c = container.getBoundingClientRect();
					const r = itemDiv.getBoundingClientRect();
					const target =
						container.scrollTop +
						(r.top - c.top) -
						(c.height / 2 - r.height / 2);
					finalScrollTop = Math.max(0, Math.min(target, maxScrollTop));
				}
			} else {
				const c = container.getBoundingClientRect();
				const r = itemDiv.getBoundingClientRect();
				if (c.height <= 0 || r.height <= 0) return;
				const target =
					container.scrollTop + (r.top - c.top) - (c.height / 2 - r.height / 2);
				finalScrollTop = Math.max(0, Math.min(target, maxScrollTop));
			}

			targetScrollTopRef.current = finalScrollTop;
			previousActiveIndex.current = index;
			ensureScrollAnimationRunning();
		},
		[maxIndex, getItemId, ensureScrollAnimationRunning],
	);

	useEffect(() => {
		if (enableAutoScroll && activeIndex !== null && activeIndex !== undefined) {
			scrollToCenter(activeIndex);
		}
	}, [activeIndex, enableAutoScroll, scrollToCenter]);

	useEffectOnce(() => {
		return () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			lastTimeRef.current = null;
		};
	});

	useInputAction(InputAction.CommonMenuNavUp, () => {
		if (modalState) {
			return;
		}

		if (activeIndex === null) {
			if (shouldSkipInstalled) {
				let index = maxIndex;
				while (handleCheckInstall(index)) {
					index--;
					if (index < 0) index = maxIndex;
				}
				handleSelect(index);
				return;
			}

			handleSelect(maxIndex);
			return;
		}

		let currentIndex = activeIndex;
		if (shouldSkipInstalled) {
			let nextIndex = currentIndex - 1;

			while (nextIndex !== activeIndex) {
				if (nextIndex < 0) nextIndex = maxIndex;
				if (!handleCheckInstall(nextIndex)) {
					currentIndex = nextIndex;
					break;
				}
				nextIndex--;
			}
			handleSelect(currentIndex);
			return;
		}

		const nextIndex = currentIndex - 1 < 0 ? maxIndex : currentIndex - 1;
		handleSelect(nextIndex);
	});

	useInputAction(InputAction.CommonMenuNavDown, () => {
		if (modalState) {
			return;
		}

		if (activeIndex === null) {
			if (shouldSkipInstalled) {
				let index = 0;
				while (handleCheckInstall(index)) {
					index++;
					if (index > maxIndex) index = 0;
				}
				handleSelect(index);
				return;
			}

			handleSelect(0);
			return;
		}

		let currentIndex = activeIndex;
		if (shouldSkipInstalled) {
			let nextIndex = currentIndex + 1;

			while (nextIndex !== activeIndex) {
				if (nextIndex > maxIndex) nextIndex = 0;
				if (!handleCheckInstall(nextIndex)) {
					currentIndex = nextIndex;
					break;
				}
				nextIndex++;
			}
			handleSelect(currentIndex);
			return;
		}

		const nextIndex = currentIndex + 1 > maxIndex ? 0 : currentIndex + 1;
		handleSelect(nextIndex);
	});

	useEffectOnce(() => {
		return () => {
			handleSelect(null);
		};
	});

	return (
		<StyledContainer {...props}>
			<StyledTopContainer>
				<img src={icon} />
				<Text>{title}</Text>
				<Text>
					{activeIndex !== null ? (
						<Fragment>
							<span>{(activeIndex || 0) + 1}</span> / {maxIndex + 1}
						</Fragment>
					) : (
						<Fragment>
							<span>0</span> / {maxIndex + 1}
						</Fragment>
					)}
				</Text>
			</StyledTopContainer>
			<ScrollContainer
				width={380}
				height={height || 489}
				showMask
				style={{ marginTop: 20 }}
			>
				<StyledListContent>{children}</StyledListContent>
			</ScrollContainer>
			{!hideBack && (
				<StyledButtonContainer>
					<Button
						color={ButtonColors.Default}
						height={ButtonHeight.Height50}
						onClick={handleBack}
						style={{ fontSize: 16 }}
					>
						<StyledButtonContent>
							<img src={arrowLeft} />
							<Text>Вернуться назад</Text>
						</StyledButtonContent>
					</Button>
				</StyledButtonContainer>
			)}
		</StyledContainer>
	);
};

export { ShopListWithScroll };
